import {
    Card,
    CardContent,
    Typography,
    Chip,
    CircularProgress,
    IconButton,
    Tooltip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import { formatTimestamp, maskEmail, maskId, getStatusColor } from '../../../utils/formatters';
import { dashboardAPI } from '../../../utils/dashboardAPI.js';
import { useAuth } from '../../../AuthContext';
import { useRealtimeTrigger } from '../../../hooks/useRealtimeTrigger';

const MessageQueueTable = ({ onShowDetails, onRetry, onDelete, autoRefresh }) => {
    const { token } = useAuth();
    const queryClient = useQueryClient();

    useRealtimeTrigger('notify', 'notifications', () => {
        queryClient.invalidateQueries({ queryKey: [ 'messages' ] });
    });

    const columns = [
        {
            field: 'actions',
            headerName: 'Actions',
            width: 150,
            sortable: false,
            renderCell: (params) => (
                <div>
                    <IconButton
                        size="small"
                        onClick={ () => onShowDetails(params.row) }
                        title="View Details"
                    >
                        <VisibilityIcon fontSize="small" />
                    </IconButton>

                    { params.row.status === 'failed' && (
                        <IconButton
                            size="small"
                            onClick={ () => onRetry(params.row.id) }
                            title="Retry"
                            color="primary"
                        >
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    ) }

                    <IconButton
                        size="small"
                        onClick={ () => onDelete(params.row.id) }
                        title="Delete"
                        color="error"
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </div>
            )
        },
        {
            field: 'id',
            headerName: 'ID',
            width: 120,
            valueFormatter: (value) => maskId(value)
        },
        {
            field: 'type',
            headerName: 'Type',
            width: 80,
            renderCell: (params) => (
                <Chip
                    label={ params.value || 'unknown' }
                    size="small"
                    variant="outlined"
                />
            )
        },
        {
            field: 'recipient',
            headerName: 'Recipient',
            width: 200,
            valueFormatter: (value) => {
                if (!value) return 'N/A';
                return value.includes('@') ? maskEmail(value) : value;
            }
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 120,
            renderCell: (params) => {
                const chip = (
                    <Chip
                        label={ params.value || 'unknown' }
                        color={ getStatusColor(params.value) }
                        size="small"
                    />
                );

                // Show tooltip with error if status is not 'sent' and error exists
                if (params.value !== 'sent' && params.row.error) {
                    return (
                        <Tooltip title={ params.row.error } arrow>
                            { chip }
                        </Tooltip>
                    );
                }

                return chip;
            }
        },
        {
            field: 'attempts',
            headerName: 'Retries',
            width: 80,
            type: 'number'
        },
        {
            field: 'createdAt',
            headerName: 'Created',
            width: 180,
            valueFormatter: (value) => formatTimestamp(value)
        },
        {
            field: 'scheduledTime',
            headerName: 'Scheduled',
            width: 180,
            valueFormatter: (value) => formatTimestamp(value)
        }
    ];
    // Use react-query to fetch and cache messages.
    // tuned: messages change frequently so poll every 15s, keep short staleTime
    const { data, isFetching } = useQuery({
        queryKey: [ 'messages' ],
        queryFn: () => dashboardAPI.getMessages({ limit: 100, status: 'failed' }, token),
        staleTime: 10000,
        retry: 1,
        refetchInterval: autoRefresh ? 15000 : false
    });

    const messages = Array.isArray(data) ? data : [];

    return (
        <Card sx={ { mb: 3 } }>
            <CardContent>
                <div style={ { display: 'flex', alignItems: 'center', gap: 8 } }>
                    <Typography variant="h6" gutterBottom sx={ { mr: 1 } }>
                        Queue Messages
                    </Typography>
                    { isFetching && (
                        <Chip
                            size="small"
                            icon={ <CircularProgress size={ 14 } /> }
                            label="Updating…"
                            variant="outlined"
                        />
                    ) }
                </div>
                <div style={ { height: 400, width: '100%' } }>
                    <DataGrid
                        rows={ messages || [] }
                        columns={ columns }
                        pagination
                        paginationMode="client"
                        pageSizeOptions={ [ 25, 50, 100 ] }
                        initialState={ {
                            pagination: { paginationModel: { pageSize: 25 } },
                            sorting: { sortModel: [ { field: 'createdAt', sort: 'desc' } ] }
                        } }
                        disableRowSelectionOnClick
                        sx={ {
                            '& .MuiDataGrid-cell': {
                                borderBottom: '1px solid #e0e0e0'
                            }
                        } }
                    />
                </div>
            </CardContent>
        </Card>
    );
};

export default MessageQueueTable;
